import { useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { toast } from 'react-toastify';
import './addDropdown.css';
import AddIcon from '../../../assets/icons/trvto_add_icon.svg';
import RemoveIcon from '../../../assets/icons/trvto_remove_icon.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { addDropdownSubmit, editDropdownSubmit } from '../dropdownFun/dropdownCRUD';
import BackIcon from '../../../assets/icons/trvto_ArrowBack.svg';
import { CAMP_FILTER_FIELDS, campFilterOptions } from '../../../utils/campFilterOptions';

/*
 * Camp filters: what each filter filters on in the camp search sidebar
 * (backend: utils/search/campFilters.js). The option "Name" is picked from
 * the values the stay form saves (utils/campFilterOptions.js), so every
 * option finds stays; the display name fills in and can be changed.
 */
const blankRow = () => ({ name: '', displayName: '' });
const key = (v) => String(v || '').trim().toLowerCase();

const DropdownUpload = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dropdownData, setDropdownData] = useState({
        id: '',
        type: '',
        heading: '',
        field: '',
        dropdown: [{ name: '', displayName: '' }]
    });

    const isCampFilter = location.state?.filter !== 'dropdown' && dropdownData.type === 'camp';
    const fieldConfig = isCampFilter ? CAMP_FILTER_FIELDS.find((f) => f.value === dropdownData.field) : null;
    const fieldOptions = fieldConfig ? campFilterOptions(fieldConfig.value) : [];
    const labelOf = (name) => fieldOptions.find((o) => key(o.value) === key(name))?.label;

    // what's missing before it can be saved (Submit stays off until empty)
    const rows = dropdownData.dropdown || [];
    const filled = rows.filter((r) => r.name?.trim() || r.displayName?.trim());
    const names = filled.map((r) => key(r.name));
    const problems = [];
    if (!dropdownData.type || dropdownData.type === 'Select') problems.push('Choose a type');
    if (!dropdownData.heading?.trim()) problems.push('Enter a heading');
    if (isCampFilter && !dropdownData.field) problems.push('Choose what it filters on');
    if (!filled.length) problems.push('Add at least one option');
    if (filled.some((r) => !r.name?.trim() || !r.displayName?.trim()))
        problems.push('Every option needs a name and a display name');
    if (new Set(names).size !== names.length) problems.push('The same option is added twice');
    if (fieldConfig && filled.some((r) => r.name && !labelOf(r.name)))
        problems.push('Replace the options marked “not used on stays”');

    useEffect(() => {
        if (!location.state?.row) return;
        setDropdownData({
            ...dropdownData,
            id: location.state.row._id,
            type: location.state.row.type,
            heading: location.state.row.heading,
            field: location.state.row.field || '',
            dropdown: location.state.row.dropdown
        });
    }, [location.state]);

    const handleAddField = () => {
        setDropdownData((prev) => ({
            ...prev,
            dropdown: [...prev.dropdown, { name: '', displayName: '' }]
        }));
    };

    const handleRemoveField = (index) => {
        setDropdownData((prev) => {
            const left = prev.dropdown.filter((_, i) => i !== index);
            return { ...prev, dropdown: left.length ? left : [blankRow()] };
        });
    };

    // camp: pick a value the stays use; the display name follows unless it was edited
    const handlePickName = (index, value) => {
        setDropdownData((prev) => ({
            ...prev,
            dropdown: prev.dropdown.map((item, i) => {
                if (i !== index) return item;
                const autoName = !item.displayName || item.displayName === labelOf(item.name);
                return { name: value, displayName: autoName ? labelOf(value) || '' : item.displayName };
            })
        }));
    };

    // a different "filter on" has different values — start the options again
    const handleFieldChange = (value) => {
        setDropdownData((prev) => ({
            ...prev,
            field: value,
            dropdown: prev.dropdown.some((r) => r.name) ? [blankRow()] : prev.dropdown
        }));
    };

    const handleInputChange = (index, field, value) => {
        setDropdownData((prev) => ({
            ...prev,
            dropdown: prev.dropdown.map((item, i) => (i === index ? { ...item, [field]: value } : item))
        }));
    };

    const dropdownDataSubmit = async () => {
        if (problems.length) {
            toast.error(problems[0]);
            return;
        }
        const payload = {
            ...dropdownData,
            heading: dropdownData.heading.trim(),
            dropdown: filled.map((r) => ({ ...r, name: r.name.trim(), displayName: r.displayName.trim() }))
        };
        if (dropdownData.id) {
            const res = await editDropdownSubmit(dropdownData.id, payload, setLoading, location.state.filter);
            if (res === 200) {
                navigate(-1);
            }
        } else {
            const res = await addDropdownSubmit(payload, setLoading, location.state.filter);
            if (res === 200) {
                navigate(-1);
            }
        }
    };

    const handleType = (e) => {
        const value = e.target.value;
        setDropdownData((prev) => ({ ...prev, type: value }));
    };

    return (
        <div>
            <button className='backBtn' onClick={() => navigate(-1)}>
                <img src={BackIcon} alt='back' />
                {`Back to ${location.state.filter === 'dropdown' ? 'Dropdown' : 'Filter'}`}
            </button>
            <h1 className='orgPackUpH1'>{`Create New ${
                location.state.filter === 'dropdown' ? 'Dropdown' : 'Filter'
            }`}</h1>
            <div className='orgPackUpSecDiv'>
                <div className='supDropHeading'>
                    <div className='directInDiv'>
                        <label className='directInLabel'>Type</label>
                        <select value={dropdownData.type} name='type' onChange={handleType} className='supportSelect'>
                            <option value='Select'>Select</option>
                            <option value='trip'>Trip</option>
                            <option value='camp'>Camp</option>
                            <option value='guide'>Guide</option>
                            <option value='activity'>Activity</option>
                        </select>
                    </div>
                    <div className='directInDiv'>
                        <label className='directInLabel'>Heading</label>
                        <div className='directInInputDiv'>
                            <input
                                value={dropdownData?.heading}
                                name='heading'
                                onChange={(e) =>
                                    setDropdownData({
                                        ...dropdownData,
                                        heading: e.target.value
                                    })
                                }
                                type='text'
                                className='directInInput'
                                placeholder='Heading'
                            ></input>
                        </div>
                    </div>
                </div>
                {isCampFilter && (
                    <div className='supDropHeading'>
                        <div className='directInDiv'>
                            <label className='directInLabel'>Filter on</label>
                            <select
                                value={dropdownData.field}
                                name='field'
                                onChange={(e) => handleFieldChange(e.target.value)}
                                className='supportSelect'
                            >
                                <option value=''>Select</option>
                                {CAMP_FILTER_FIELDS.map((f) => (
                                    <option key={f.value} value={f.value}>
                                        {f.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <p className='campFilterNote'>
                            {fieldConfig
                                ? 'Names are the values used on the stays, so every option finds stays.'
                                : 'Choose what this filter matches on the stays.'}
                        </p>
                    </div>
                )}
                <div className='dropdownSubCon'>
                    {dropdownData?.dropdown?.map((item, index) => (
                        <div key={index} className='dropdownFieldRow'>
                            <div className='inputGroup'>
                                <label className='directInLabel'>Name</label>
                                {fieldConfig ? (
                                    <>
                                        <select
                                            name={`name-${index}`}
                                            value={item.name}
                                            className='supportSelect'
                                            onChange={(e) => handlePickName(index, e.target.value)}
                                        >
                                            <option value=''>Select</option>
                                            {item.name && !labelOf(item.name) && (
                                                <option value={item.name}>{item.name} (not used on stays)</option>
                                            )}
                                            {fieldConfig.groups.map((g) => {
                                                const opts = g.options.map((o) => (
                                                    <option
                                                        key={o.value}
                                                        value={o.value}
                                                        // already added in another row
                                                        disabled={key(o.value) !== key(item.name) && names.includes(key(o.value))}
                                                    >
                                                        {key(o.label) === key(o.value) ? o.label : `${o.label} (${o.value})`}
                                                    </option>
                                                ));
                                                return g.group ? (
                                                    <optgroup key={g.group} label={g.group}>
                                                        {opts}
                                                    </optgroup>
                                                ) : (
                                                    opts
                                                );
                                            })}
                                        </select>
                                        {item.name && !labelOf(item.name) && (
                                            <small className='campFilterWarn'>No stay uses this value — pick one from the list</small>
                                        )}
                                    </>
                                ) : (
                                    <input
                                        type='text'
                                        name={`name-${index}`}
                                        value={item.name}
                                        placeholder='Name'
                                        onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                                    />
                                )}
                            </div>
                            <div className='inputGroup'>
                                <label className='directInLabel'>Display Name</label>
                                <input
                                    type='text'
                                    name={`displayName-${index}`}
                                    value={item.displayName}
                                    placeholder='Display Name'
                                    onChange={(e) => handleInputChange(index, 'displayName', e.target.value)}
                                />
                            </div>
                            <div>
                                {index === dropdownData.dropdown.length - 1 && (
                                    <button type='button' onClick={handleAddField} className='btnStyle'>
                                        <img src={AddIcon} alt='add' width={32} height={32} />
                                    </button>
                                )}
                                <button
                                    type='button'
                                    onClick={() => handleRemoveField(index)}
                                    className='removeBtn btnStyle'
                                >
                                    <img src={RemoveIcon} alt='remove' width={32} height={32} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                {loading && (
                    <div className='loadingBackground'>
                        <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                    </div>
                )}

                {problems.length > 0 && filled.length > 0 && (
                    <p className='campFilterWarn submitNote'>{problems[0]}</p>
                )}
                <button
                    type='button'
                    className='orgPackUpSubmitBtn blueBtn'
                    onClick={() => dropdownDataSubmit()}
                    disabled={problems.length > 0 || loading}
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

export default DropdownUpload;
